import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { recordSpotSubmission } from '../utils/profileManager.js'

const CATEGORIES = [
  { value: 'cultural',   label: '🏛️ Cultural' },
  { value: 'monuments',  label: '🗿 Monument' },
  { value: 'activities', label: '🎯 Activity' },
  { value: 'locals',     label: '🏠 Local' },
  { value: 'food',       label: '🍽️ Food & Drink' },
]

export default function SubmitSpotForm({ userData, onClose, onSubmitted }) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    name:          '',
    description:   '',
    category:      'cultural',
    mapsLink:      '',
    submitterName: userData?.userName || '',
    submitterEmail:'',
    attributionOptin: false,        // Sprint 3 — default anonymous
    contributorHandle: '',
  })
  const [status, setStatus] = useState('idle') // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('')

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }))

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.description.trim() || !form.submitterEmail.trim()) {
      setErrorMsg(t('community.missingFields'))
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.submitterEmail)) {
      setErrorMsg(t('community.invalidEmail'))
      return
    }

    setStatus('loading')
    setErrorMsg('')
    try {
      const resp = await fetch('./api/submit-spot.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          country:        userData?.country || '',
          city:           userData?.destination || '',
          name:           form.name.trim(),
          description:    form.description.trim(),
          category:       form.category,
          mapsLink:       form.mapsLink.trim(),
          submitterName:  form.submitterName.trim(),
          submitterEmail: form.submitterEmail.trim(),
          attributionOptin:  form.attributionOptin,
          contributorHandle: form.attributionOptin ? form.contributorHandle.trim() : '',
        }),
      })
      const data = await resp.json()
      if (!data.success) throw new Error(data.error || 'Submission failed')
      await recordSpotSubmission(data.submissionId, userData?.country || '', userData?.destination || '', form.name.trim())
      setStatus('success')
      onSubmitted?.(data.submissionId)
    } catch (e) {
      setErrorMsg(e.message)
      setStatus('error')
    }
  }

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: '12px',
    border: '1.5px solid #E8DDD0', fontSize: '14px', color: '#3d3028',
    background: 'white', boxSizing: 'border-box', outline: 'none',
    fontFamily: 'inherit',
  }
  const labelStyle = { fontSize: '11px', fontWeight: '700', color: '#B8A082', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }

  if (status === 'success') {
    return (
      <div style={{ padding: '32px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📍</div>
        <div style={{ fontSize: '20px', fontWeight: '800', color: '#8B7355', marginBottom: '8px' }}>
          {t('community.submitted')}
        </div>
        <div style={{ fontSize: '14px', color: '#B8A082', lineHeight: 1.5, marginBottom: '24px' }}>
          {t('community.submittedDesc')}
        </div>
        <button onClick={onClose} style={{
          background: '#C4714A', color: 'white', border: 'none',
          borderRadius: '12px', padding: '12px 28px', fontSize: '14px',
          fontWeight: '700', cursor: 'pointer',
        }}>
          {t('community.close')}
        </button>
      </div>
    )
  }

  return (
    <div style={{ padding: '0 0 24px' }}>
      {/* Handle */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 16px' }}>
        <div style={{ width: '40px', height: '4px', background: '#D4C5B0', borderRadius: '999px' }} />
      </div>

      <div style={{ padding: '0 20px' }}>
        <div style={{ fontSize: '18px', fontWeight: '800', color: '#3d3028', marginBottom: '4px' }}>
          {t('community.formTitle')}
        </div>
        <div style={{ fontSize: '13px', color: '#B8A082', marginBottom: '16px' }}>
          {userData?.destination}, {userData?.country}
        </div>

        {/* Community pitch + reward */}
        <div style={{ background: '#F2DDD2', borderRadius: '12px', padding: '12px 14px', marginBottom: '20px' }}>
          <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#5C4A32', lineHeight: 1.5 }}>{t('community.pitch')}</p>
          <p style={{ margin: 0, fontSize: '13px', color: '#A85B38', fontWeight: 700, lineHeight: 1.4 }}>🎁 {t('community.reward')}</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle}>{t('community.placeName')}</label>
            <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)}
              placeholder={t('community.placeNamePlaceholder')} maxLength={120} />
          </div>

          <div>
            <label style={labelStyle}>{t('community.category')}</label>
            <select style={inputStyle} value={form.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label style={labelStyle}>{t('community.description')}</label>
            <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
              value={form.description} onChange={e => set('description', e.target.value)}
              placeholder={t('community.descriptionPlaceholder')} maxLength={400} />
          </div>

          <div>
            <label style={labelStyle}>{t('community.mapsLink')} <span style={{ fontWeight: '400', textTransform: 'none', fontSize: '10px' }}>({t('community.optional')})</span></label>
            <input style={inputStyle} value={form.mapsLink} onChange={e => set('mapsLink', e.target.value)}
              placeholder="https://maps.google.com/..." type="url" />
          </div>

          <div style={{ borderTop: '1px solid #f0ebe4', paddingTop: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#B8A082', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>
              {t('community.yourInfo')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>{t('community.yourName')}</label>
                <input style={inputStyle} value={form.submitterName} onChange={e => set('submitterName', e.target.value)}
                  placeholder={t('community.yourNamePlaceholder')} maxLength={80} />
              </div>
              <div>
                <label style={labelStyle}>{t('community.yourEmail')}</label>
                <input style={inputStyle} type="email" value={form.submitterEmail} onChange={e => set('submitterEmail', e.target.value)}
                  placeholder="you@email.com" />
              </div>

              {/* Sprint 3 — opt-in public attribution (default anonymous) */}
              <div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#5d4f3f', lineHeight: 1.4 }}>
                  <input type="checkbox" checked={form.attributionOptin}
                    onChange={e => set('attributionOptin', e.target.checked)}
                    style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span>{t('community.attributionOptin')}</span>
                </label>
                {form.attributionOptin && (
                  <input style={{ ...inputStyle, marginTop: '8px' }} value={form.contributorHandle}
                    onChange={e => set('contributorHandle', e.target.value)}
                    placeholder={t('community.contributorHandlePlaceholder')} maxLength={60} />
                )}
                {!form.attributionOptin && (
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#B8A082', fontStyle: 'italic' }}>
                    {t('community.attributionAnonNote')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {errorMsg && (
          <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fce4ec', borderRadius: '10px', color: '#c62828', fontSize: '13px' }}>
            {errorMsg}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '14px', borderRadius: '12px', border: '1.5px solid #E8DDD0',
            background: 'white', color: '#8B7355', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
          }}>
            {t('community.cancel')}
          </button>
          <button onClick={handleSubmit} disabled={status === 'loading'} style={{
            flex: 2, padding: '14px', borderRadius: '12px', border: 'none',
            background: status === 'loading' ? '#C4B49A' : '#8B7355',
            color: 'white', fontSize: '14px', fontWeight: '700', cursor: status === 'loading' ? 'default' : 'pointer',
          }}>
            {status === 'loading' ? t('community.submitting') : `📍 ${t('community.submit')}`}
          </button>
        </div>
      </div>
    </div>
  )
}
